import React, { FC, useCallback, useEffect, useState } from "react";
import { styled } from "@mui/system";
import Popover from "@mui/material/Popover";
import Checkbox from "@mui/material/Checkbox";
import { COLORS, FONT_FAMILIES, FONT_WEIGHTS, RADII, SPACINGS } from "../../common/consts";
import { getAddonStatus, setFeatureSeats } from "../../services/firebase";
import { AddonKey, AddonStatus, AddonStatusResult } from "../../types/addons";

/**
 * Which products a licensed user may use, edited in place on the Users table.
 * The chip shows what they have; clicking it opens a list to tick products on
 * and off. Status is fetched once for the whole table and shared.
 */

let cached: AddonStatusResult | null = null;
let inflight: Promise<AddonStatusResult> | null = null;
const listeners = new Set<(s: AddonStatusResult) => void>();

const publish = (next: AddonStatusResult) => {
  cached = next;
  listeners.forEach((fn) => fn(next));
};

const loadStatus = (): Promise<AddonStatusResult> => {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    inflight = getAddonStatus()
      .then((s) => {
        publish(s);
        return s;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
};

const Trigger = styled("button")<{ muted?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${SPACINGS.xs};
  max-width: 100%;
  height: 1.75rem;
  padding: 0 ${SPACINGS.md};
  border-radius: ${RADII.full};
  border: 1px solid var(--color-backgroundBrand, ${COLORS.backgroundBrand});
  background: ${({ muted }) => (muted ? "transparent" : `var(--color-backgroundBrandLight, ${COLORS.backgroundBrandLight})`)};
  color: ${({ muted }) =>
    muted ? `var(--color-foregroundSecondary, ${COLORS.foregroundSecondary})` : `var(--color-foregroundBrand, ${COLORS.foregroundBrand})`};
  font-family: ${FONT_FAMILIES.regular};
  font-size: 0.8125rem;
  font-weight: ${FONT_WEIGHTS.medium};
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background: var(--color-tabHoverBg, ${COLORS.tabHoverBg});
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const Panel = styled("div")`
  min-width: 260px;
  padding: ${SPACINGS.sm} 0;
  background: var(--color-backgroundWhite, ${COLORS.backgroundWhite});
`;

const Head = styled("div")`
  padding: ${SPACINGS.xs} ${SPACINGS.lg} ${SPACINGS.sm};
  color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
  font-size: 0.75rem;
  letter-spacing: 0.025rem;
  text-transform: uppercase;
`;

const Option = styled("label")<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${SPACINGS.sm};
  padding: ${SPACINGS.xs} ${SPACINGS.lg};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  &:hover {
    background: var(--color-tabHoverBg, ${COLORS.tabHoverBg});
  }
`;

const OptionText = styled("div")`
  display: flex;
  flex-direction: column;
`;

const OptionName = styled("span")`
  font-size: 0.875rem;
  color: var(--color-foregroundPrimary, ${COLORS.foregroundPrimary});
`;

const OptionHint = styled("span")`
  font-size: 0.75rem;
  color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
`;

const Foot = styled("div")`
  padding: ${SPACINGS.sm} ${SPACINGS.lg} ${SPACINGS.xs};
  color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
  font-size: 0.75rem;
`;

interface Props {
  email: string;
  /** False when the user holds no boring license, so nothing can be assigned. */
  licensed: boolean;
}

const seatsUsed = (addon: AddonStatus) => addon.assigned.length;

const FeatureAccessCell: FC<Props> = ({ email, licensed }) => {
  const [status, setStatus] = useState<AddonStatusResult | null>(cached);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [saving, setSaving] = useState<AddonKey | null>(null);
  const key = (email || "").toLowerCase();

  useEffect(() => {
    listeners.add(setStatus);
    loadStatus().catch(() => setStatus(cached));
    return () => {
      listeners.delete(setStatus);
    };
  }, []);

  const available = (status?.addons || []).filter((a) => a.enabled);
  const mine = available.filter((a) => a.assigned.includes(key));

  const toggle = useCallback(
    async (addon: AddonStatus, on: boolean) => {
      const next = new Set(addon.assigned);
      if (on) next.add(key);
      else next.delete(key);
      setSaving(addon.key);
      try {
        publish(await setFeatureSeats(addon.key, Array.from(next)));
      } finally {
        setSaving(null);
      }
    },
    [key]
  );

  if (!licensed) return <OptionHint>Needs a license</OptionHint>;
  if (!status) return <OptionHint>Loading</OptionHint>;
  if (available.length === 0) return <OptionHint>No products enabled</OptionHint>;

  const label = mine.length === 0 ? "Email only" : mine.length === available.length ? "All products" : mine.map((a) => a.name).join(", ");

  return (
    <>
      <Trigger type="button" muted={mine.length === 0} onClick={(e) => setAnchor(e.currentTarget)} title={label}>
        {label}
      </Trigger>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { style: { borderRadius: 8, boxShadow: "rgba(55,50,73,.04) 0 2px 1px 0, rgba(55,50,73,.14) 0 4px 5px 0, rgba(69,66,98,.08) 0 1px 7px 0" } } }}
      >
        <Panel>
          <Head>Product access</Head>
          {available.map((addon) => {
            const checked = addon.assigned.includes(key);
            const full = addon.seatLimit !== null && !checked && seatsUsed(addon) >= addon.seatLimit;
            return (
              <Option key={addon.key} disabled={full || saving === addon.key}>
                <Checkbox
                  size="small"
                  checked={checked}
                  disabled={full || saving === addon.key}
                  onChange={(e) => toggle(addon, e.target.checked)}
                />
                <OptionText>
                  <OptionName>{addon.name}</OptionName>
                  <OptionHint>
                    {addon.seatLimit === null
                      ? "Included for every licensed user"
                      : `${seatsUsed(addon)} of ${addon.seatLimit} seats used${full ? ", none left" : ""}`}
                  </OptionHint>
                </OptionText>
              </Option>
            );
          })}
          <Foot>Email verification is included with every boring license.</Foot>
        </Panel>
      </Popover>
    </>
  );
};

export default FeatureAccessCell;
export { loadStatus as preloadAddonStatus };
